import { Stack, Text, Divider, Accordion } from '@ltvco/refresh-lib/theme';

import { formatLongDate } from '../../../shared';
import {
  SectionStack,
  PositionCard,
  PositionDetailsList,
  DetailListItemBox,
} from '../components';

import type { JudgePosition } from '../../../types';

const DetailListItem = ({
  label,
  value,
  show = true,
}: {
  label: string;
  value: string | React.ReactNode;
  show?: boolean;
}) => {
  if (!show) {
    return null;
  }

  return (
    <DetailListItemBox component="li">
      <Text variant="body2">
        <Text component="span" fontWeight="bold">
          {label}:
        </Text>{' '}
        {value}
      </Text>
    </DetailListItemBox>
  );
};

const PositionTitle = ({
  title,
  organization,
  dateStart,
  dateEnd,
}: {
  title: string;
  organization: string;
  dateStart?: string;
  dateEnd?: string;
}) => (
  <Text variant="body1">
    <Text component="span" fontWeight="bold">
      {title ? title.toUpperCase() : 'N/A'}
    </Text>
    {organization && (
      <>
        <Text component="span" fontStyle="italic">
          {' '}
          at{' '}
        </Text>
        <Text component="span" fontWeight="bold">
          {organization}
        </Text>
      </>
    )}
    {dateStart && (
      <Text component="span" fontStyle="italic">
        {` (${formatLongDate(dateStart)} - ${
          dateEnd ? formatLongDate(dateEnd) : 'Present'
        })`}
      </Text>
    )}
  </Text>
);

interface JudgeCareerHistoryProps {
  judicialPositions: JudgePosition[];
  nonJudicialPositions: JudgePosition[];
}

export const JudgeCareerHistory = ({
  judicialPositions,
  nonJudicialPositions,
}: JudgeCareerHistoryProps) => {
  if (judicialPositions.length === 0 && nonJudicialPositions.length === 0) {
    return null;
  }

  return (
    <SectionStack sx={{ mt: 2 }}>
      <Text variant="h5" sx={{ mb: 2 }}>
        Career History
      </Text>
      <Divider sx={{ mb: 1 }} />

      {judicialPositions.length > 0 && (
        <Accordion display={`Judicial Positions (${judicialPositions.length})`}>
          <Stack spacing={1}>
            {judicialPositions.map((position, index) => (
              <PositionCard key={index}>
                <PositionTitle
                  title={position.position_type}
                  organization={position.court_full_name || ''}
                  dateStart={position.date_start || ''}
                  dateEnd={position.date_termination || ''}
                />
                <PositionDetailsList component="ul" spacing={0.5}>
                  <DetailListItem
                    label="Appointed by"
                    value={position.appointer}
                    show={!!position.appointer}
                  />
                  <DetailListItem
                    label="Selected by"
                    value={`${position.selection_method}${
                      position.date_nominated
                        ? ` on ${formatLongDate(position.date_nominated)}`
                        : ''
                    }`}
                    show={!!position.selection_method}
                  />
                  <DetailListItem
                    label="Senior Status Date"
                    value={
                      position.date_retirement
                        ? formatLongDate(position.date_retirement)
                        : 'N/A'
                    }
                    show={!!position.date_retirement}
                  />
                  <DetailListItem
                    label="Judicial Committee Action Date"
                    value={
                      position.date_judicial_committee_action
                        ? formatLongDate(
                            position.date_judicial_committee_action
                          )
                        : 'N/A'
                    }
                    show={!!position.date_judicial_committee_action}
                  />
                  <DetailListItem
                    label="Nomination Hearing Date"
                    value={
                      position.date_hearing
                        ? formatLongDate(position.date_hearing)
                        : 'N/A'
                    }
                    show={!!position.date_hearing}
                  />
                  <DetailListItem
                    label="Confirmation Date"
                    value={
                      position.date_confirmation
                        ? formatLongDate(position.date_confirmation)
                        : 'N/A'
                    }
                    show={!!position.date_confirmation}
                  />
                  <DetailListItem
                    label="Termination Reason"
                    value={position.termination_reason}
                    show={!!position.termination_reason}
                  />
                </PositionDetailsList>
              </PositionCard>
            ))}
          </Stack>
        </Accordion>
      )}

      {nonJudicialPositions.length > 0 && (
        <Accordion
          display={`Non-Judicial Positions (${nonJudicialPositions.length})`}
        >
          <Stack spacing={1}>
            {nonJudicialPositions.map((position, index) => (
              <PositionCard key={index}>
                <PositionTitle
                  title={position.job_title}
                  organization={position.organization_name || ''}
                  dateStart={position.date_start || ''}
                  dateEnd={position.date_termination || ''}
                />
              </PositionCard>
            ))}
          </Stack>
        </Accordion>
      )}
    </SectionStack>
  );
};
